/**
 * MAUKAU — product-page behaviour that the theme does not provide.
 *
 * WHY THIS FILE EXISTS. Both behaviours below started as inline <script> blocks inside
 * `sections/main-product.liquid` and `snippets/product-gallery.liquid`, and both were shipped
 * BROKEN in exactly the same way, a day apart:
 *
 *   - the reassurance slider defined a custom element inline, so the element upgraded the moment its
 *     opening tag was parsed — before any of its slides existed — and its init found zero slides;
 *   - the gallery's scroll-to-variant reached for `document.forms[…]` while the form was still
 *     further down the page, found nothing, and never bound a listener.
 *
 * An inline classic script runs DURING parsing, so anything it touches must already be above it in
 * the document. That is a trap, not a rule worth living with: this file is loaded with `defer`, so it
 * runs after parsing and the DOM is simply there. The theme's own components never hit the problem
 * because its bundle is a deferred module — this file now matches that.
 *
 * Everything here is self-discovering from the DOM (no Liquid interpolation), which is what makes it
 * a plain asset rather than a Liquid template.
 */
(function () {
  'use strict';

  /**
   * Reassurance strip — autoplay.
   *
   * The sliding itself is NOT implemented here. The markup is a `<scroll-carousel>`, the theme's own
   * carousel primitive, so motion, touch swipe, drag, snapping and keyboard all come from the theme.
   * This adds the one thing the theme does not do by itself: advance it on a timer.
   *
   * It scrolls to the slide's own offset rather than calling the carousel's `select()`. `select()`
   * pages by measuring `offsetLeft` against the nearest positioned ancestor, and these slides sit
   * inside a <ul> rather than directly under the carousel, so its paging landed between two slides.
   * Differencing two offsets cancels whatever the offset parent is.
   *
   * A move never starts while one is still running, and every settle reads the real scroll position
   * back and corrects to the nearest slide — so a resting position that is not a slide cannot
   * survive. See `onSettled` for the two ways it used to get stuck.
   *
   * Autoplay pauses on hover, on focus and when the tab is hidden, and stops for good the moment
   * someone scrolls or drags it themselves — once a person is driving, moving it under them is
   * hostile.
   */
  function startAutoplay(slider) {
    if (slider.dataset.autoplayBound === 'true') return;
    slider.dataset.autoplayBound = 'true';

    var slides = Array.prototype.slice.call(slider.querySelectorAll('.reassurance-slider__slide'));
    if (slides.length < 2) return;

    var seconds = parseFloat(slider.getAttribute('interval')) || 4;
    var timer = null;
    var settleTimer = null;
    var userDriven = false;
    var moving = false;
    var index = 0;

    // Where each slide starts, relative to the first. Differencing two offsetLefts cancels whatever
    // the offset parent is, so this is correct however the slides are nested.
    function offsetOf(i) {
      return slides[i].offsetLeft - slides[0].offsetLeft;
    }

    // Which slide the strip is ACTUALLY resting on, read back from the scroll position rather than
    // assumed from a counter. This is what stops the two drifting apart.
    function nearestIndex() {
      var pos = slider.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(offsetOf(i) - pos);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    /**
     * Called when a scroll settles — whether ours or the reader's.
     *
     * FIXES THE FREEZE (Michael, 2026-08-12: "sometimes freezes mid-transition, getting stuck
     * between two lines"). Two causes, both handled here:
     *
     *   1. the interval fired again while a smooth scroll was still animating, so the second call
     *      retargeted mid-flight and the strip came to rest between two slides. `moving` now gates
     *      that: no new move starts until the previous one has settled.
     *   2. a smooth scroll can end slightly off its target — interrupted by a resize, by the tab
     *      losing focus mid-animation, or by scroll-snap arbitrating — and nothing put it right,
     *      because the counter still believed it had arrived. Settling now READS BACK the real
     *      position and, if it is not on a slide, snaps to the nearest one instantly.
     *
     * So the strip can no longer hold a position that is not a slide: every settle either confirms
     * one or corrects to one.
     */
    function onSettled() {
      moving = false;
      index = nearestIndex();

      var drift = Math.abs(slider.scrollLeft - offsetOf(index));
      if (drift > 2) {
        slider.scrollTo({ left: offsetOf(index), behavior: 'auto' });
      }
    }

    function scheduleSettle() {
      clearTimeout(settleTimer);
      // Fallback for engines without `scrollend`, and a backstop for a smooth scroll that never
      // reports finishing. Generous enough not to cut a normal animation short.
      settleTimer = setTimeout(onSettled, 900);
    }

    function advance() {
      if (userDriven || moving) return;
      moving = true;
      index = (nearestIndex() + 1) % slides.length;
      slider.scrollTo({ left: offsetOf(index), behavior: 'smooth' });
      scheduleSettle();
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function start() {
      if (userDriven || timer) return;
      timer = setInterval(advance, seconds * 1000);
    }

    function surrender() {
      userDriven = true;
      stop();
    }

    if ('onscrollend' in window) {
      slider.addEventListener('scrollend', function () {
        clearTimeout(settleTimer);
        onSettled();
      });
    }

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', start);
    slider.addEventListener('pointerdown', surrender, { passive: true });
    slider.addEventListener('touchstart', surrender, { passive: true });
    slider.addEventListener('wheel', surrender, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        // A smooth scroll does not always complete while the tab is hidden, so re-align on return
        // rather than resuming from a position that may be between slides.
        onSettled();
        start();
      }
    });

    // Re-align after a resize: slide offsets are width-derived, so a rotation or a desktop resize
    // moves every target and the current resting position stops being a slide.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(onSettled, 200);
    });

    start();
  }

  /**
   * Desktop grid — scroll to the selected variant's image.
   *
   * The theme's own handler resolves the new media position and then calls
   * `carousel.select(position)` — it moves a CAROUSEL INDEX and nothing else. In the desktop grid
   * layout the media list is a CSS grid with every image stacked down the page, so there is no
   * scroll axis inside the element for `select()` to act on and the call is a no-op. Mobile and
   * thumbnails mode both keep a real carousel, which is why they already work.
   *
   * So this is the small addition that was missing, not a fix to something broken. It listens to the
   * same `variant:change` event and only acts when the gallery is in grid mode AND the viewport is at
   * the desktop breakpoint — below that the same markup IS a carousel the theme already drives, and
   * acting there would fight it.
   *
   * `block: 'nearest'` rather than `'start'`: in a 1×1 grid the images are large and the right one is
   * often already on screen, where yanking the page to pin it to the top would be worse than doing
   * nothing.
   */
  function bindVariantMediaScroll(gallery) {
    var formId = gallery.getAttribute('form');
    if (!formId) return;

    var form = document.forms[formId];
    if (!form || form.dataset.variantMediaScrollBound === 'true') return;

    form.dataset.variantMediaScrollBound = 'true';

    form.addEventListener('variant:change', function (event) {
      if (!gallery.classList.contains('product-gallery--desktop-grid')) return;
      if (!window.matchMedia('(min-width: 1000px)').matches) return;

      var variant = event.detail && event.detail.variant;
      var mediaId = variant && variant.featured_media && variant.featured_media.id;
      if (!mediaId) return;

      var target = gallery.querySelector('[data-media-id="' + mediaId + '"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  /**
   * Price inside the "Ajouter au panier" button — keep it equal to the price shown above it.
   *
   * WHY THIS EXISTS. The price in the button is rendered server-side from
   * `product.selected_or_first_available_variant.price`, in two mirrored places
   * (`snippets/buy-buttons.liquid` and the sticky bar in `sections/main-product.liquid`). That was
   * written on the assumption that a size change is a VARIANT change, which `<product-rerender>`
   * would pick up.
   *
   * On the personalisable products that assumption is simply false. `tapis-vinyle-personnalise-
   * mosaic-gris` has ONE variant, "Default Title", at 59,90 € — the size selector on that page is not
   * the theme's variant picker at all, it is an option injected by the Product Personalizer app, and
   * "66 x 132 cm" carries a +40 € add-on in the app's own config. So the customer picks a size, the
   * app rewrites the main price to 99,90 €, no variant ever changes, nothing re-renders, and the
   * button keeps saying 59,90 € — the price it was born with. That is what Karine reported on
   * 2026-08-19, and it is why no server-side fix can work: there is no server round-trip to hook.
   *
   * The invariant that was actually broken is "the button agrees with the price above it", so that is
   * what this enforces, literally: whatever the main price element ends up displaying gets copied
   * into every `.buy-buttons__price` on the page. It is deliberately indifferent to WHY the price
   * moved — a real variant change, an app add-on, or anything added later — because the failure mode
   * being fixed is precisely that we guessed wrong about the cause of the movement once already.
   *
   * The observer watches the whole product section rather than the price node, because both the price
   * block and the buy-buttons block are REPLACED (not edited) when the theme re-renders on a genuine
   * variant change — a listener bound to either node would not survive it. Writes are skipped when
   * the text already matches, so the observer cannot retrigger itself.
   *
   * IT ALSO REPAIRS THE MAIN PRICE (2026-08-19, second report). The app does not edit the price, it
   * overwrites the element with a plain string — which destroys the two pieces of markup the theme
   * put there: the `.price-decimals` span that keeps the cents visually quiet, and the screen-reader
   * label. So on a personalisable product the price silently reverted to full-size cents the moment
   * a size was picked, above the title AND, once the button started mirroring it, inside the button.
   * `quietDecimals` rebuilds exactly what `snippets/price-money.liquid` renders — euros full size,
   * cents in a `.price-decimals` span, currency symbol back at full size after it.
   *
   * The rebuild only runs on a section whose price was BORN with de-emphasised decimals, so a shop
   * setting turned off is respected rather than re-imposed here. The separator is read off the price
   * itself (last `.` or `,` before exactly two digits), never assumed, for the same reason
   * `price-money` reads it from the money format: a second market would change it.
   *
   * `repairs` is a circuit breaker. Restoring the span is a DOM write inside the very element the app
   * owns; if some future version of it wrote plain text straight back, the two would ping-pong. After
   * 30 repairs in two seconds this stops touching the main price and keeps only the button mirroring,
   * which is the behaviour that matters and is the one that cannot loop.
   */
  function quietDecimals(text, srOnlyHtml) {
    var trimmed = text.replace(/\s+/g, ' ').trim();

    // Greedy prefix, so this lands on the LAST separator followed by exactly two digits: "1.234,56 €"
    // splits after the comma, not after the thousands dot.
    var parts = trimmed.match(/^(.*)([.,])(\d{2})(\D*)$/);
    if (!parts) return (srOnlyHtml || '') + trimmed;

    return (
      (srOnlyHtml || '') +
      parts[1] +
      '<span class="price-decimals">' + parts[2] + parts[3] + '</span>' +
      parts[4]
    );
  }
  function bindButtonPriceSync(section) {
    if (section.dataset.buttonPriceSyncBound === 'true') return;
    section.dataset.buttonPriceSyncBound = 'true';

    var priceIn = function () { return section.querySelector('.product-info__price sale-price'); };

    var born = priceIn();
    if (!born) return;

    // Captured from the server render, BEFORE the app has had a chance to overwrite either of them.
    var quietByDesign = !!born.querySelector('.price-decimals');
    var srOnly = born.querySelector('.sr-only');
    var srOnlyHtml = srOnly ? srOnly.outerHTML : '';

    var repairs = 0;
    var repairWindowOpenedAt = 0;

    function repairMainPrice(source) {
      if (!quietByDesign) return;
      if (source.querySelector('.price-decimals')) return;

      var now = Date.now();
      if (now - repairWindowOpenedAt > 2000) {
        repairWindowOpenedAt = now;
        repairs = 0;
      }
      if (++repairs > 30) return;

      source.innerHTML = quietDecimals(source.textContent, srOnlyHtml);
    }

    function priceHtmlForButton(source) {
      // The screen-reader label ("Prix de vente") lives inside the price element and must not travel
      // into the button, where it would be announced a second time.
      var clone = source.cloneNode(true);
      Array.prototype.forEach.call(clone.querySelectorAll('.sr-only'), function (node) {
        node.parentNode.removeChild(node);
      });

      var html = clone.innerHTML.trim();
      if (quietByDesign && !clone.querySelector('.price-decimals')) {
        html = quietDecimals(clone.textContent, '');
      }
      return html;
    }

    function sync() {
      var source = priceIn();
      if (!source) return;

      repairMainPrice(source);

      var html = priceHtmlForButton(source);
      if (!html) return;

      Array.prototype.forEach.call(section.querySelectorAll('.buy-buttons__price'), function (target) {
        if (target.innerHTML.trim() !== html) target.innerHTML = html;
      });
    }

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        sync();
      });
    });

    observer.observe(section, { childList: true, subtree: true, characterData: true });
    sync();
  }

  function init() {
    document.querySelectorAll('.product-info__reassurance-slider').forEach(startAutoplay);
    document.querySelectorAll('product-gallery[form]').forEach(bindVariantMediaScroll);
    document.querySelectorAll('.product-info__price').forEach(function (priceEl) {
      var section = priceEl.closest('.shopify-section') || document.body;
      bindButtonPriceSync(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
